import * as pdfjsLib from 'pdfjs-dist';

export interface OutlineItem {
  title: string;
  pageNumber: number;
  children?: OutlineItem[];
}

/**
 * 從 PDF 原生大綱（bookmarks）提取章節目錄
 * @param pdfUrl PDF 檔案路徑
 * @returns 提取的大綱列表
 */
export async function extractNativeOutline(pdfUrl: string): Promise<OutlineItem[]> {
  try {
    const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
    const outline = await pdf.getOutline();

    if (!outline || outline.length === 0) {
      return [];
    }

    // 將 PDF.js 的 outline 格式轉換此的格式
    const convertOutlineItem = async (item: any): Promise<OutlineItem | null> => {
      try {
        // step 1：預設頁碼是 1
        let pageNumber = 1;

        // 解析 dest 獲取頁碼
        if (item.dest) {
          let dest = item.dest;

          // step 2：解析 item.dest 找頁碼
          // dest 可能有兩種格式包括 string（命名目的地）或 array（直接目的地）
          if (typeof dest === 'string') {
            dest = await pdf.getDestination(dest);
          }

          if (Array.isArray(dest) && dest.length > 0) {
            const pageRef = dest[0];
            // pageRef 可能是頁面引用物件或直接的頁碼
            if (typeof pageRef === 'object' && pageRef !== null) {
              const pageIndex = await pdf.getPageIndex(pageRef);
              pageNumber = pageIndex + 1; // 轉換為 1-based
            } else if (typeof pageRef === 'number') {
              pageNumber = pageRef + 1;
            }
          }
        }

        // step 3：組成你的格式
        const outlineItem: OutlineItem = {
          title: item.title || 'Untitled',
          pageNumber,
        };

        // step 4：遞迴處理子項目
        if (item.items && item.items.length > 0) {
          const children = await Promise.all(
            item.items.map((child: any) => convertOutlineItem(child))
          );
          outlineItem.children = children.filter((c): c is OutlineItem => c !== null);
        }

        return outlineItem;
      } catch (error) {
        console.warn('[Native Outline] 處理項目失敗:', item.title, error);
        return null;
      }
    };

    const result = await Promise.all(
      outline.map((item: any) => convertOutlineItem(item))
    );

    return result.filter((item): item is OutlineItem => item !== null);
  } catch (error) {
    console.error('[Native Outline] 提取失敗:', error);
    return [];
  }
}

/**
 * 基於字體大小啟發式規則提取標題
 * @param pdfUrl PDF 檔案路徑
 * @returns 提取的標題列表
 */
export async function extractHeadingsFromText(pdfUrl: string): Promise<OutlineItem[]> {
  try {
    const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
    const headings: OutlineItem[] = [];

    // 第一步：掃描前 10 頁，收集字體大小資訊
    // 範例：整包檔案跑完後收到[10, 10, 12, 12, 12, 14, 10, 12, 12, 50]
    const fontSizes: number[] = [];
    const samplePages = Math.min(pdf.numPages, 10);

    for (let i = 1; i <= samplePages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();

      textContent.items.forEach((item: any) => {
        if (item.transform && item.transform[0]) {
          fontSizes.push(item.transform[0]);
        }
      });
    }

    if (fontSizes.length === 0) {
      return [];
    }

    // 計算字體大小中位數（作為內文基準）
    fontSizes.sort((a, b) => a - b);
    const medianFontSize = fontSizes[Math.floor(fontSizes.length / 2)] || 12;

    // 標題閾值：大於中位數的 1.4 倍（可調整）
    // 範例：中位數 = 12 , 標題門檻 = 12 × 1.4 = 16.8
    const headingThreshold = medianFontSize * 1.4;

    // 第二步：掃描所有頁面，提取大字體文字
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      let currentLine = '';
      let currentFontSize = 0;
      let lastY = 0;

      textContent.items.forEach((item: any) => {
        const fontSize = item.transform?.[0] || 0;
        const text = item.str.trim();
        const y = item.transform?.[5] || 0;

        // 跳過空白和特殊字元
        if (text.length === 0 || /^[\d\s\.、，。！？]+$/.test(text)) {
          return;
        }

        if (fontSize >= headingThreshold) {
          // 判斷是否換行（Y 座標變化超過閾值）
          const isNewLine = Math.abs(y - lastY) > fontSize * 0.5;

          if (isNewLine && currentLine.length > 0) {
            // 保存前一行標題
            if (currentLine.length >= 2 && currentLine.length <= 100) {
              headings.push({
                title: currentLine.trim(),
                pageNumber: pageNum,
              });
            }
            currentLine = text;
          } else {
            // 同一行，合併文字
            currentLine += (currentLine.length > 0 ? ' ' : '') + text;
          }

          currentFontSize = fontSize;
          lastY = y;
        } else {
          // 遇到非標題文字，保存當前標題
          if (currentLine.length >= 2 && currentLine.length <= 100) {
            headings.push({
              title: currentLine.trim(),
              pageNumber: pageNum,
            });
          }
          currentLine = '';
          currentFontSize = 0;
        }
      });

      // 保存頁面最後的標題
      if (currentLine.length >= 2 && currentLine.length <= 100) {
        headings.push({
          title: currentLine.trim(),
          pageNumber: pageNum,
        });
      }
    }

    // 智能去重和過濾
    const filteredHeadings = headings.filter((heading, index, self) => {
      // 1. 移除太短的標題（可能是頁碼或噪音）
      if (heading.title.length < 3) return false;

      // 2. 移除連續重複的標題
      if (index > 0) {
        const prev = self[index - 1];
        if (heading.title === prev.title && heading.pageNumber === prev.pageNumber) {
          return false;
        }
      }

      return true;
    });

    // 3. 每頁只保留第一個標題（避免同一頁有多個誤判）
    const pageGroups = new Map<number, OutlineItem[]>();

    filteredHeadings.forEach(heading => {
      if (!pageGroups.has(heading.pageNumber)) {
        pageGroups.set(heading.pageNumber, []);
      }
      pageGroups.get(heading.pageNumber)!.push(heading);
    });

    // 每頁只取第一個標題（通常是最重要的）
    const finalHeadings = Array.from(pageGroups.values()).map(group => group[0]);
    return finalHeadings;

  } catch (error) {
    console.error('[Heading Extract] 提取失敗:', error);
    return [];
  }
}